import { isEqual } from 'radash';

function getOfferPrice(offerParts: OfferPart[]): { currency: string; value: number } {
    const currency = offerParts[0]?.price.currency || '';
    const value = offerParts.reduce((sum, offerPart) => sum + (analyticsGetOfferPartPrice(offerPart) || 0), 0);

    return { currency, value: Number.parseFloat(value.toFixed(2)) };
};

/**
 * Provides functions to track ecommerce events in the CHECKOUT DOMAIN of checkout domain (based on cart store)
 */
export function useCheckoutAnalytics() {
    const cartStore = useCartStore();
    const checkoutAnalyticsStore = useCheckoutAnalyticsStore();

    function analyticsTrackAddToCart(item: Product | OfferAncillary) {
        try {
            const items: EcommerceDataItem[] = [];
            const offerParts = 'offerParts' in item ? item.offerParts : item.offer?.offerParts || [];

            if (offerParts.length === 0) {
                return;
            }

            for (const offerPart of offerParts) {
                const ecommerceItem = analyticsGetEcommerceItem({
                    offerPart,
                    parent: 'type' in item ? undefined : item,
                    passengers: cartStore.passengers,
                });

                items.push(ecommerceItem);
            }

            analyticsTrackEcommerce('add_to_cart', {
                ...getOfferPrice(offerParts),
                items,
            });
        }
        catch (error) {
            handleError(error, false);
        }
    }

    function analyticsTrackRemoveFromCart(item: Product | OfferAncillary) {
        try {
            const items: EcommerceDataItem[] = [];
            const offerParts = 'offerParts' in item ? item.offerParts : item.offer?.offerParts || [];

            for (const offerPart of offerParts) {
                const ecommerceItem = analyticsGetEcommerceItem({
                    offerPart,
                    parent: 'type' in item ? undefined : item,
                    passengers: cartStore.passengers,
                });

                items.push(ecommerceItem);
            }

            analyticsTrackEcommerce('remove_from_cart', {
                ...getOfferPrice(offerParts),
                items,
            });
        }
        catch (error) {
            handleError(error, false);
        }
    }

    function analyticsTrackCartAncillaryChanges(ancillaries: OfferAncillary[]) {
        try {
            const addedAncillaries = ancillaries.filter(ancillary => !checkoutAnalyticsStore.trackedAncillaries.some(existing => isEqual(existing.offerParts, ancillary.offerParts)));
            const removedAncillaries = checkoutAnalyticsStore.trackedAncillaries.filter(ancillary => !ancillaries.some(existing => isEqual(existing.offerParts, ancillary.offerParts)));

            for (const ancillary of addedAncillaries) {
                analyticsTrackAddToCart(ancillary);
            }

            for (const ancillary of removedAncillaries) {
                analyticsTrackRemoveFromCart(ancillary);
            }

            if (addedAncillaries.length || removedAncillaries.length) {
                checkoutAnalyticsStore.trackedAncillaries = ancillaries;
            }
        }
        catch (error) {
            handleError(error, false);
        }
    }

    function analyticsTrackCartEprToggle(item: ProductP2P, isDisabled: boolean) {
        try {
            const items: EcommerceDataItem[] = [];
            const offerParts = item.offer?.offerParts.filter(offerPart => offerPart.type === 'ReservationOfferPart') || [];

            for (const offerPart of offerParts) {
                const ecommerceItem = analyticsGetEcommerceItem({
                    offerPart,
                    parent: 'type' in item ? undefined : item,
                    passengers: cartStore.passengers,
                });

                items.push(ecommerceItem);
            }

            if (isDisabled) {
                analyticsTrackEcommerce('remove_from_cart', {
                    ...getOfferPrice(offerParts),
                    items,
                });
            }
            else {
                analyticsTrackEcommerce('add_to_cart', {
                    ...getOfferPrice(offerParts),
                    items,
                });
            }
        }
        catch (error) {
            handleError(error, false);
        }
    }

    /**
     *
     */
    function analyticsTrackUpsellEvent({ title, action }: { title: string; action: 'impression' | 'click' }) {
        try {
            analyticsTrackEvent({
                event: 'add_option',
                element_type: 'button',
                element_text: title,
                element_context: 'upsell',
                element_action: action,
            }, 'analyticsTrackUpsellEvent');
        }
        catch (error) {
            handleError(error, false);
        }
    }

    /**
     *
     */
    function analyticsTrackCrossSellEvent({ groupTitle, title, action }: { groupTitle: string; title: string; action: 'impression' | 'click' }) {
        try {
            analyticsTrackEvent({
                event: 'add_option',
                element_type: 'link',
                element_name: groupTitle,
                element_text: title,
                element_context: 'crosssell',
                element_action: action,
            }, 'analyticsTrackCrossSellEvent');
        }
        catch (error) {
            handleError(error, false);
        }
    }

    /**
     *
     */
    function analyticsTrackCheckoutCartEvent(
        eventName: EcommerceEventName,
        quote?: Quote,
        selectedOffersAncillary: OfferAncillary[] = [],
        additionalEcommerceData?: Partial<EcommerceData>,
    ) {
        try {
            const items: EcommerceDataItem[] = [];
            const cart_type = [...new Set(cartStore.products.map(product => product.isUpsell))].length > 1 ? 'mixed' : 'single';

            cartStore.products.forEach((product) => {
                product.offer?.offerParts.forEach((offerPart) => {
                    const item = analyticsGetEcommerceItem({ offerPart, parent: product, passengers: cartStore.passengers });
                    items.push(item);
                });
            });

            selectedOffersAncillary.forEach((offerAncillary) => {
                if (!offerAncillary.offerParts[0]) {
                    return;
                }
                const item = analyticsGetEcommerceItem({ offerPart: offerAncillary.offerParts[0], passengers: cartStore.passengers });
                items.push(item);
            });

            analyticsTrackEcommerce(eventName, {
                currency: quote?.price.currency,
                value: quote?.price.amount ? quote?.price.amount / 100 : undefined,
                items,
                cart_type,
                ...additionalEcommerceData,
            });
        }
        catch (error) {
            handleError(error, false);
        }
    };

    return {
        analyticsTrackAddToCart,
        analyticsTrackRemoveFromCart,
        analyticsTrackCartAncillaryChanges,
        analyticsTrackCartEprToggle,
        analyticsTrackUpsellEvent,
        analyticsTrackCrossSellEvent,
        analyticsTrackCheckoutCartEvent,
    };
}
